<template>
  <header ref="headerRef" :class="['header-click', { shrink: headerShrink }]">
    <!-- =========================
         NÍVEL 1 — LOGO + LOGIN
    ========================== -->
    <div class="lvl lvl-1">
      <div class="header-container">
        <router-link to="/home" class="logo" id="header-logo" data-gtm="click_header_logo" aria-label="Ir para a Home">
          <img src="/img/logo-preto.webp" alt="Click Sorvetes" />
        </router-link>

        <div class="spacer"></div>

        <div class="acoes">
          <router-link to="/login" class="acao acao-login" id="header-login" data-gtm="click_header_login" aria-label="Área do associado">
            <i class="bi bi-person"></i>
            <span class="acao-texto">Login</span>
          </router-link>

          <button class="btn-hamburguer" id="btn-menu-mobile-open" data-gtm="click_open_mobile_menu" aria-label="Abrir menu" @click="toggleMenu">
            <i class="bi bi-list"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- =========================
         NÍVEL 2 — MENU PRINCIPAL (DESKTOP)
         VEM 100% DO /public/database/headerMenu.json
    ========================== -->
    <div class="lvl lvl-2">
      <div class="header-container header-container-center">
        <nav class="menu-desktop" aria-label="Menu principal">
          <ul class="menu-main">
            <li
              v-for="grupo in headerMenuAtivo"
              :key="grupo.id"
              class="menu-group"
              :class="{ open: openDropdown === grupo.id && grupo.tipo === 'dropdown' }"
              @mouseenter="onDesktopEnter(grupo)"
              @mouseleave="onDesktopLeave(grupo)"
            >
              <!-- LINK DIRETO -->
              <router-link v-if="grupo.tipo === 'link'" :to="grupo.to" class="menu-item" :id="`menu-desktop-${slug(grupo.label)}`" :data-gtm="`click_menu_desktop_${slugUnder(grupo.label)}`">
                {{ grupo.label }}
              </router-link>

              <!-- DROPDOWN -->
              <button
                v-else
                class="menu-trigger"
                type="button"
                aria-haspopup="true"
                :aria-expanded="openDropdown === grupo.id"
                :id="`menu-${grupo.id}`"
                :data-gtm="`click_menu_desktop_${slugUnder(grupo.label)}`"
              >
                {{ grupo.label }} <span class="caret">▾</span>
              </button>

              <div v-if="grupo.tipo === 'dropdown'" class="dropdown" role="menu" :aria-label="grupo.label">
                <router-link
                  v-for="item in grupo.itensAtivos"
                  :key="item.to"
                  :to="item.to"
                  class="dropdown-item"
                  :id="`menu-desktop-${slug(item.nome)}`"
                  :data-gtm="`click_menu_desktop_${slugUnder(item.nome)}`"
                >
                  {{ item.nome }}
                </router-link>
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- =========================
         MENU MOBILE (COLLAPSE) — MESMO JSON
    ========================== -->
    <nav class="menu-mobile" :class="{ ativo: menuAberto }" aria-label="Menu mobile">
      <button class="btn-fechar" id="btn-menu-mobile-close" data-gtm="click_close_mobile_menu" aria-label="Fechar menu" @click="fecharMenu">✕</button>

      <router-link to="/login" class="mobile-login" id="menu-mobile-login" data-gtm="click_menu_mobile_login" @click="fecharMenu">
        <i class="bi bi-person"></i>
        <span><Link:atom></Link:atom>ogin</span>
      </router-link>

      <div class="mobile-collapse">
        <div v-for="grupo in headerMenuAtivo" :key="grupo.id" class="mc-group">
          <!-- LINK DIRETO -->
          <router-link
            v-if="grupo.tipo === 'link'"
            :to="grupo.to"
            class="mc-head mc-direct"
            :id="`menu-mobile-${slug(grupo.label)}`"
            :data-gtm="`click_menu_mobile_${slugUnder(grupo.label)}`"
            @click="fecharMenu"
          >
            <span>{{ grupo.label }}</span>
            <span class="mc-caret open">›</span>
          </router-link>

          <!-- COLLAPSE -->
          <button
            v-else
            class="mc-head"
            type="button"
            :aria-expanded="mobileOpen === grupo.id"
            :id="`mobile-${grupo.id}`"
            :data-gtm="`click_menu_mobile_group_${slugUnder(grupo.label)}`"
            @click="toggleMobile(grupo.id)"
          >
            <span>{{ grupo.label }}</span>
            <span class="mc-caret" :class="{ open: mobileOpen === grupo.id }">▾</span>
          </button>

          <div v-if="grupo.tipo === 'dropdown'" class="mc-body" v-show="mobileOpen === grupo.id">
            <router-link
              v-for="item in grupo.itensAtivos"
              :key="item.to"
              :to="item.to"
              class="mc-item"
              :id="`menu-mobile-${slug(item.nome)}`"
              :data-gtm="`click_menu_mobile_${slugUnder(item.nome)}`"
              @click="fecharMenu"
            >
              {{ item.nome }}
            </router-link>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { getJson } from "@/utils";

const headerRef = ref(null);

const headerMenu = ref([]);

const menuAberto = ref(false);
const headerShrink = ref(false);

const openDropdown = ref(null);
const mobileOpen = ref(null);

const toggleMenu = () => (menuAberto.value = !menuAberto.value);

const fecharMenu = () => {
  menuAberto.value = false;
  mobileOpen.value = null;
  openDropdown.value = null;
};

function toggleMobile(key) {
  mobileOpen.value = mobileOpen.value === key ? null : key;
}

watch(menuAberto, (aberto) => {
  document.body.classList.toggle("no-scroll", aberto);
  if (!aberto) {
    mobileOpen.value = null;
    openDropdown.value = null;
  }
});

const handleScroll = () => {
  headerShrink.value = window.scrollY > 50;
};

function normalize(str) {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function slug(str) {
  return normalize(str).replace(/ /g, "-");
}

function slugUnder(str) {
  return normalize(str).replace(/ /g, "_");
}

const headerMenuAtivo = computed(() => {
  const groups = Array.isArray(headerMenu.value) ? headerMenu.value : [];
  return groups
    .filter((g) => g && g.ativo === true)
    .map((g) => {
      if (g.tipo === "dropdown") {
        const itens = Array.isArray(g.itens) ? g.itens : [];
        const itensAtivos = itens.filter((it) => it && it.ativo === true && it.to && it.nome);
        return { ...g, itensAtivos };
      }
      return g;
    })
    .filter((g) => (g.tipo === "dropdown" ? (g.itensAtivos?.length || 0) > 0 : !!g.to));
});

function onDesktopEnter(grupo) {
  if (grupo?.tipo !== "dropdown") return;
  openDropdown.value = grupo.id;
}

function onDesktopLeave(grupo) {
  if (grupo?.tipo !== "dropdown") return;
  openDropdown.value = null;
}

let ro = null;
let onResize = null;

function atualizarHeaderAltura() {
  const el = headerRef.value;
  if (!el) return;
  document.documentElement.style.setProperty("--header-altura", `${el.offsetHeight}px`);
}

onMounted(async () => {
  window.addEventListener("scroll", handleScroll);

  try {
    const data = await getJson("database/headerMenu.json");
    headerMenu.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("[Header] Falha ao carregar database/headerMenu.json", err);
    headerMenu.value = [];
  }

  atualizarHeaderAltura();

  ro = new ResizeObserver(atualizarHeaderAltura);
  if (headerRef.value) ro.observe(headerRef.value);

  onResize = () => atualizarHeaderAltura();
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  if (onResize) window.removeEventListener("resize", onResize);
  if (ro && headerRef.value) ro.unobserve(headerRef.value);
});
</script>

<style scoped>
.header-click {
  position: fixed;
  inset: 0 0 auto 0;
  width: 100%;
  z-index: 2000;
  transition: 0.2s ease;
}

.header-container {
  width: 100%;
  max-width: 1240px;
  padding-inline: 24px;
  margin: 0 auto;
  display: flex;
  align-items: center;
}

.header-container-center {
  justify-content: center;
}

.spacer {
  flex: 1;
}

.lvl {
  width: 100%;
  display: flex;
  align-items: center;
}

.lvl-1 {
  height: 64px;
  background: #fff;
  /* background: #f8f9fc; */
  color: rgba(35, 35, 35, 0.85);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.header-click.shrink .lvl-1 {
  height: 56px;
}

.logo img {
  width: 150px;
  transition: 0.2s ease;
}

.header-click.shrink .logo img {
  width: 128px;
}

.acoes {
  display: flex;
  align-items: center;
  gap: 14px;
}

.acao {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: 0.2s ease;
  color: rgba(35, 35, 35, 0.78);
  font-size: 1.05rem;
}

.acao:hover {
  color: var(--rosa-principal, #ff5c8a);
}

.acao-texto {
  display: none;
  font-size: 0.95rem;
  font-weight: 400;
  color: inherit;
}

.btn-hamburguer {
  font-size: 2rem;
  background: none;
  border: none;
  color: rgba(35, 35, 35, 0.78);
  display: inline-flex;
  cursor: pointer;
  padding: 0;
}

.lvl-2 {
  height: 44px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.header-click.shrink .lvl-2 {
  height: 40px;
}

.menu-desktop {
  display: none;
  width: 100%;
}

.menu-main {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 26px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.menu-item,
.menu-trigger {
  appearance: none;
  background: none;
  border: none;
  color: rgba(35, 35, 35, 0.78);
  font-weight: 400;
  font-size: 0.95rem;
  letter-spacing: 0.2px;
  padding: 10px 0;
  cursor: pointer;
  transition: 0.18s ease;
  white-space: nowrap;
}

.menu-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.caret {
  font-size: 0.9em;
  opacity: 0.75;
}

.menu-item:hover,
.menu-trigger:hover {
  color: rgba(35, 35, 35, 0.92);
}

.router-link-active.menu-item {
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 6px;
  text-decoration-color: rgba(0, 0, 0, 0.22);
}

.menu-group {
  position: relative;
}

.menu-group::after {
  content: "";
  position: absolute;
  left: -12px;
  right: -12px;
  top: 100%;
  height: 14px;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(12px);
  min-width: 260px;
  background: #fff;
  padding: 12px 14px;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  display: none;
}

.menu-group.open .dropdown {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dropdown-item {
  color: rgba(35, 35, 35, 0.78);
  font-weight: 400;
  font-size: 0.95rem;
  padding: 6px 8px;
  border-radius: 10px;
  transition: 0.18s ease;
}

.dropdown-item:hover {
  color: rgba(35, 35, 35, 0.92);
  background: rgba(0, 0, 0, 0.03);
}

.menu-mobile {
  position: fixed;
  inset: 0;
  display: none;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  z-index: 4000;
  padding: 72px 22px 24px;
  overflow-y: auto;
}

.menu-mobile.ativo {
  display: flex;
}

.btn-fechar {
  position: absolute;
  top: 18px;
  right: 18px;
  background: none;
  border: none;
  color: rgba(35, 35, 35, 0.8);
  font-size: 2.1rem;
  cursor: pointer;
}

.mobile-login {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: rgba(35, 35, 35, 0.88);
  font-weight: 500;
  font-size: 1.05rem;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.mobile-collapse {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mc-group {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 10px;
}

.mc-head {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  background: none;
  border: none;
  color: rgba(35, 35, 35, 0.9);
  font-weight: 500;
  font-size: 1.05rem;
  cursor: pointer;
  text-align: left;
}

.mc-direct {
  text-decoration: none;
}

.mc-caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 0.95rem;
  opacity: 0.7;
  transform: rotate(0deg);
  transition: 0.18s ease;
}

.mc-caret.open {
  transform: rotate(180deg);
}

.mc-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 0 6px 0;
}

.mc-item {
  display: block;
  color: rgba(35, 35, 35, 0.78);
  font-weight: 400;
  font-size: 1rem;
  padding: 6px 0;
  text-decoration: none;
}

.mc-item:hover {
  color: rgba(35, 35, 35, 0.95);
}

@media (min-width: 900px) {
  .menu-desktop {
    display: block;
  }

  .btn-hamburguer,
  .menu-mobile {
    display: none !important;
  }

  .acao-texto {
    display: inline;
  }
}
/* =========================
   MOBILE — HIERARQUIA DE PESO
   Guarda-chuva > opções
========================= */

@media (max-width: 899px) {
  /* Títulos / guarda-chuvas */
  .mc-head {
    font-weight: 550; /* antes: 500 → mantém, é o peso guia */
    color: rgba(35, 35, 35, 0.95);
  }

  /* Itens internos */
  .mc-item {
    font-weight: 300; /* mais leve que o guarda-chuva */
    color: rgba(35, 35, 35, 0.75);
  }

  .mc-item:hover {
    color: rgba(35, 35, 35, 0.9);
  }
  .lvl-2 {
    height: 0 !important;
    min-height: 0 !important;
    padding: 0 !important;
    border: none !important;
    overflow: hidden;
  }
}
</style>
